/**
 * Algolia Product Helpers
 * 
 * Provides shared functions for rendering product cards consistently
 * across different Algolia implementations (InstantSearch, Autocomplete).
 * 
 */
window.addToCartWithDrawerUpdate = async function(variantId, productCardElement = null) {
  if (!variantId) {
    return { success: false, error: 'No variant ID provided' };
  }

  try {
    const itemData = {
      id: variantId,
      quantity: 1
    };

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [itemData],
        sections: ['cart-drawer', 'cart-icon-bubble']
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Request failed');
    }

    const data = await response.json();

    // New cart: re-render via the cart-drawer component, then open it.
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:open'));

    return { success: true, data };

  } catch (error) {
    console.error('Error adding to cart:', error);
    return { success: false, error: error.message || 'Failed to add to cart' };
  }
};
window.algoliaProductHelpers = {
  CURRENCY_DISPLAY_CONFIG: {
    // Use 'symbol' for '$' or 'code' for 'USD'
    usd_display: 'symbol', // 'symbol' | 'code'
  },
  TOOLTIP_CONFIG: {
    show_on_price_variation_only: true,
  },

  /**
   * Formats a price with the correct currency symbol or code.
   * @param {number} price - The price value.
   * @param {string} currency - The currency code (e.g., 'USD', 'CAD').
   * @returns {string} - The formatted price string.
   */
  formatPriceWithCurrency(price, currency) {
    const formattedPrice = price?.toFixed(2);
    const lowerCaseCurrency = currency ? currency.toLowerCase() : '';

    if (lowerCaseCurrency === 'cad') {
      return `$${formattedPrice} CAD`;
    }

    if (lowerCaseCurrency === 'usd') {
      if (this.CURRENCY_DISPLAY_CONFIG.usd_display === 'code') {
        return `${formattedPrice} USD`;
      }
      return `$${formattedPrice} USD`;
    }

    // Fallback for other currencies or if currency is not provided
    return `${formattedPrice} ${currency?.toUpperCase()}`;
  },

  /**
   * Creates the full HTML for a product card.
   * @param {object} hit - The Algolia hit object for the product.
   * @param {object} options - Configuration options.
   * @param {object} options.html - The preact html component.
   * @param {object} options.components - Algolia components for highlighting.
   * @param {function} options.itemLink - Function to generate the product link.
   * @param {function} options.trackSearchAttribution - Function to track clicks.
   * @returns {object} - The preact HTML for the product card.
   */
  createProductCardHTML(hit, { html, components, itemLink, trackSearchAttribution }, source) {
    const hitImage = algoliaShopify.helpers.renderImage(hit, 500);
    if (hitImage.sizes === '' || hitImage.srcset === '') {
      hitImage.src = window.fallback_logo_url;
    }
    const currentLocale = window.Shopify && Shopify.locale ? Shopify.locale : 'en';

    // Icon badges
    const badgeString = currentLocale === 'fr'
      ? hit.meta?.productdetails?.label_fr
      : hit.meta?.productdetails?.label_en;
    
    const badgeKeys = badgeString ? badgeString.split(',')
      .map(b => b.trim())
      .filter(Boolean) : [];

    // Text badges logic
    const textBadges = [];
    
    const newInLabel = currentLocale === 'fr' 
      ? hit.meta?.productdetails?.dropdown_label_fr 
      : hit.meta?.productdetails?.dropdown_label;
    if (newInLabel && newInLabel.trim() !== '' && newInLabel.trim() !== '*') {
      textBadges.push({
        type: 'new_in',
        text: window.algoliaLocales?.product?.new_in
      });
    }
    
    // textBadges.push({
    //   type: 'bestseller',
    //   text: currentLocale === 'fr' ? 'BESTSELLER' : 'BESTSELLER'
    // });

    // Price display logic
    const hasVariantPrices = hit.variants_min_price !== undefined && hit.variants_max_price !== undefined;
    const price = hasVariantPrices ? hit.variants_min_price : hit.price;
    const comparePrice = hasVariantPrices ? hit.variants_compare_at_price_min : hit.compare_at_price;
    const priceVaries = hasVariantPrices && (hit.variants_min_price !== hit.variants_max_price);
    const showComparePrice = comparePrice && comparePrice > price;
    
    const fromLabel = window.algoliaLocales?.product?.from_label;

    const pricesVary = hasVariantPrices && (
        hit.variants_min_price !== hit.variants_max_price ||
        hit.variants_compare_at_price_min !== hit.variants_compare_at_price_max
    );

    let saleBadge = null;
    if (showComparePrice) {
      const discount = ((comparePrice - price) / comparePrice) * 100;
      if (discount >= 47) {
        saleBadge = {
          type: 'final_sale',
          text: window.algoliaLocales?.product?.final_sale
        };
      } else {
        saleBadge = {
          type: 'sale',
          text: window.algoliaLocales?.product?.sale
        };
      }
    }

    const sizeOption = hit.option1;
    const sizesString = sizeOption || '';
    const sizesLabel = window.algoliaLocales?.product?.sizes_available;
    const sizesLabelMobile = window.algoliaLocales?.product?.sizes_mobile;
    const variantId = hit.objectID || '';
    const addToCartText = window.algoliaLocales?.product?.add_to_cart;
    const selectSizeText = window.algoliaLocales?.product?.select_size;


    let ratingValue = 0;
    let reviewCount = 0;
    try {
      if (hit.meta?.reviews?.rating) {
        const ratingData = typeof hit.meta.reviews.rating === 'string' 
          ? JSON.parse(hit.meta.reviews.rating)
          : hit.meta.reviews.rating;
        ratingValue = parseFloat(ratingData.value || 0);
        reviewCount = parseInt(hit.meta?.reviews?.rating_count || hit.meta?.reviews?.count || 0, 10);
      }
    } catch (e) {
      console.warn('Failed to parse rating data for product card', e);
      ratingValue = 0;
      reviewCount = 0;
    }
    
    // Determine out of stock text based on tags
    const hasComingSoonTag = hit.tags && hit.tags.includes('coming_soon');
    const outOfStockText = hasComingSoonTag
      ? window.algoliaLocales?.product?.coming_soon
      : window.algoliaLocales?.product?.out_of_stock;


    // Check if product is in winter suit collection
    const collectionsData = hit.collection_ids || [];
    const collectionsString = Array.isArray(collectionsData) ? collectionsData.join(',') : '';

    return html`
      <a
        href="${itemLink}"
        title="${algoliaShopify.helpers.fullTitle(hit.title)}"
        class="ais-hit-custom ais-product"
        data-algolia-index="${hit.index}"
        data-algolia-position="${hit.productPosition}"
        data-algolia-queryid="${hit.queryID}"
        data-algolia-objectid="${hit.objectID}"
        data-handle="${hit.handle}"
        data-variant-id="${hit.objectID}"
        data-distinct="${hit._distinct}"
        data-product-id="${hit.id}"
        data-tags="${hit.tags ? hit.tags.join(',') : ''}"
        data-collections="${collectionsString}"
        data-prices-vary="${pricesVary}"
        data-currency="${hit.currency}"
        onClick="${() => trackSearchAttribution(hit)}"
      >
        <div class="product-card__image-area">
          <div class="product-card__images-container">
            <img
              class="product-card__image"
              src="${hitImage.src}"
              srcset="${hitImage.srcset}"
              sizes="${hitImage.sizes}"
              alt="${hit.title} - ${hit.variant_title}"
            />
          </div>
          
          <div class="lhg-app-badge-anchor" 
                data-product-id="${hit.id}" 
                data-collection-ids="${JSON.stringify(Array.isArray(hit.collection_ids) ? hit.collection_ids : [hit.collection_ids])}"
                data-badge-location="product-card">
              </div>
              

              

          ${textBadges.length > 0 ? html`
            <div class="product-card__text-badges ${source === 'autocomplete' ? 'algolia-visible' : 'algolia-hidden'}">
              ${textBadges.map(badge => html`
                <div class="product-card__text-badge product-card__text-badge--${badge.type}">
                  ${badge.text}
                </div>
              `)}
            </div>
          ` : ''}

        
          <div class="product-card__sizes-desktop algolia-hidden">
            
            
            <span class="product-card__sizes-label">${sizesLabel}</span>
            <div class="product-card__sizes-list">
              <a href="${itemLink}${variantId ? `?variant=${variantId}` : ''}" class="product-card__size-button">
                ${sizesString}
              </a>
            </div>
          

            <div class="product-card__atc-container">
              <button type="button" class="product-card__atc-button" disabled>${addToCartText}</button>
              <div class="product-card__atc-error"></div>

               <div class="klaviyo-product-container hidden" id="klaviyo-data-handle-${hit.handle}" data-klaviyo-handle="${hit.handle}">
                <div class="klaviyo-button-container">
                </div>
              </div>
            </div>
          </div>
          

          ${html`
            <div class="product-card__out-of-stock algolia-hidden">
              ${outOfStockText}
            </div>
          `}
        </div>

        <div class="product-card__content">
          <h3 class="product-card__title">
            ${components.Highlight({ attribute: 'title', hit })}
          </h3>

          ${ratingValue > 0 ? html`
            <div class="product-card__rating">
              <div class="product-card__stars">
                ${Array.from({length: 5}, (_, i) => {
                  const isFullStar = i < Math.floor(ratingValue);
                  const isPartialStar = !isFullStar && i < Math.ceil(ratingValue);
                  const partialFill = isPartialStar ? (ratingValue - Math.floor(ratingValue)) * 100 : 0;
                  
                  return html`
                    <div class="product-card__star-wrapper ${isFullStar ? 'product-card__star--active' : ''} ${isPartialStar ? 'product-card__star--partial' : ''}">
                      <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg" class="product-card__star">
                        <path d="M9.44071 15.4206C9.16931 15.2561 8.829 15.2561 8.55761 15.4206L4.39532 17.9434C3.75017 18.3344 2.95263 17.7559 3.12406 17.0212L4.22755 12.2922C4.29924 11.985 4.19529 11.6633 3.95739 11.4561L0.293637 8.26557C-0.273874 7.77136 0.031211 6.83787 0.781053 6.77419L5.60934 6.36418C5.92375 6.33748 6.19766 6.13919 6.3212 5.84883L8.21605 1.39536C8.51015 0.704152 9.48985 0.704153 9.78395 1.39536L11.6788 5.84883C11.8023 6.13919 12.0762 6.33748 12.3907 6.36418L17.2189 6.77419C17.9688 6.83787 18.2739 7.77136 17.7064 8.26557L14.0426 11.4561C13.8047 11.6633 13.7008 11.985 13.7725 12.2922L14.876 17.0216C15.0474 17.7562 14.25 18.3347 13.6048 17.9438L9.44071 15.4206Z" fill="currentColor"/>
                      </svg>
                      ${isPartialStar ? html`
                        <div class="product-card__star-fill" style="width: ${partialFill}%"></div>
                      ` : ''}
                    </div>
                  `;
                })}
              </div>
              ${reviewCount > 0 ? html`
                <span class="product-card__rating-count">(${reviewCount})</span>
              ` : ''}
            </div>
          ` : ''}

          ${sizesString && hit.variants_count > 1 ? html`
            <div class="product-card__sizes product-card__sizes--mobile algolia-hidden">
              <span class="product-card__sizes-label">${sizesLabelMobile}</span>
            </div>
          ` : ''}
            <div>
            
            <div class="product-card__price">
              ${priceVaries ? html`
                <span class="product-card__price-from">${fromLabel}</span>
              ` : ''}
              <span class="product-card__price-current">
                ${this.formatPriceWithCurrency(price, hit.currency)}
              </span>
              
              ${showComparePrice ? html`
                <span class="product-card__price-original">
                  ${this.formatPriceWithCurrency(comparePrice, hit.currency)}
                </span>
                <span class="product-card__price-discount">
                  -${Math.round(((comparePrice - price) / comparePrice) * 100)}%
                </span>
              ` : ''}

              ${saleBadge ? html`
                <span class="product-card__price-discount product-card__price-badge--${saleBadge.type}">
                  ${saleBadge.text}
                </span>
              ` : ''}
            </div>

              <div class="lhg-app-badge-anchor" 
              data-product-id="${hit.id}" 
              data-collection-ids="${JSON.stringify(Array.isArray(hit.collection_ids) ? hit.collection_ids : [hit.collection_ids])}"
              data-badge-location="product-price">
              </div>
          </div>
          

        </div>
      </a>
    `;
  },

  /**
   * Fetches additional data for products (variants, secondary images) and updates the cards.
   * @param {string} containerSelector - The CSS selector for the container holding the product cards.
   */
  async fetchAdditionalProductData(containerSelector) {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    const productCards = container.querySelectorAll('.ais-hit-custom.ais-product');
    if (!productCards.length) return;

    const productHandles = Array.from(productCards)
      .map(card => card.dataset.handle)
      .filter(Boolean);

    if (!productHandles.length) return;

    console.log('🖼️ Fetching additional product data for', productHandles.length, 'products');

    try {
      const chunkSize = 20;
      const handleChunks = [];
      for (let i = 0; i < productHandles.length; i += chunkSize) {
        handleChunks.push(productHandles.slice(i, i + chunkSize));
      }

      const fetchPromises = handleChunks.map(async (chunk) => {
        const queryString = chunk.map(handle => encodeURIComponent(handle)).join(',');
        const response = await fetch(`${window.routes.search_url}?section_id=algolia-product-additional-data&q=${queryString}`);
        if (!response.ok) throw new Error(`Failed to fetch chunk: ${response.status}`);
        
        const html = await response.text();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const jsonElement = tempDiv.querySelector('#algolia-product-additional-data');
        return jsonElement ? JSON.parse(jsonElement.textContent) : {};
      });

      const chunksData = await Promise.all(fetchPromises);
      const combinedData = Object.assign({}, ...chunksData);

      for (const [handle, productData] of Object.entries(combinedData)) {
        if (!productData) continue;
        const cardElement = container.querySelector(`.ais-product[data-handle="${handle}"]`);
        if (!cardElement) continue;

        // --- RESET STATE ---
        // First, hide all dynamic elements to prevent state from previous items from leaking
        const elementsToReset = cardElement.querySelectorAll(' .product-card__text-badges, .product-card__out-of-stock, .product-card__sizes-desktop, .product-card__sizes--mobile');
        elementsToReset.forEach(el => {
          el.classList.remove('algolia-visible');
          el.classList.add('algolia-hidden');
        });
        
        // --- HANDLE BADGES (ICONS & TEXT) ---
        // const iconBadgesContainer = cardElement.querySelector('.product-card__icon-badges');
        const textBadgesContainer = cardElement.querySelector('.product-card__text-badges');
        // const hasIconBadges = iconBadgesContainer && iconBadgesContainer.querySelector('.product-card__icon-badge');

        this.handlePrimaryImageSwap(cardElement, productData);
        this.handleSecondaryImage(cardElement, productData);
        this.handleVariantData(cardElement, productData);
      }

      this.setupImageToggle();
      console.log('✅ Additional product data loaded successfully');
    } catch (error) {
      console.error('Error fetching additional product data:', error);
    }
  },

  handlePrimaryImageSwap(productCard, productData) {
    const primaryImageElement = productCard.querySelector('.product-card__image');
    if (!primaryImageElement || !productData.primary_image || !productData.primary_image.image) {
      return;
    }

    // Use the renderImage helper to get consistent responsive image attributes
    const imageFromProductData = algoliaShopify.helpers.renderImage(productData.primary_image, 500);

    const oldFilename = primaryImageElement.src.split('/').pop().split('?')[0];
    const newFilename = imageFromProductData.src.split('/').pop().split('?')[0];
    const urlsAreDifferent = oldFilename !== newFilename;

    // console.log(`🖼️ Image Swap Check for ${productData.handle}:
    //   - Old Filename: ${oldFilename}
    //   - New Filename: ${newFilename}
    //   - Filenames are different: ${urlsAreDifferent}`);

    if (urlsAreDifferent) {
      // Preload the new primary image
      const img = new Image();
      img.src = imageFromProductData.src;
      img.srcset = imageFromProductData.srcset;
      img.sizes = imageFromProductData.sizes;

      img.onload = () => {
        // Once loaded, swap the image source
        primaryImageElement.src = imageFromProductData.src;
        primaryImageElement.srcset = imageFromProductData.srcset;
        primaryImageElement.sizes = imageFromProductData.sizes;
        
        // Update alt text to remove variant-specific info
        const titleElement = productCard.querySelector('.product-card__title');
        if (titleElement) {
          primaryImageElement.alt = titleElement.textContent.trim();
        }
        console.log(`✅ New primary image loaded and swapped for ${productData.handle}`);
      };
    }
  },

  handleSecondaryImage(productCard, productData) {
    const imageContainer = productCard.querySelector('.product-card__images-container');
    if (!imageContainer || !productData.secondary_image) return;

    const existingSecondaryImage = imageContainer.querySelector('.product-card__image-secondary');
    if (existingSecondaryImage) {
      existingSecondaryImage.remove();
    }

    const imageFromProductData = algoliaShopify.helpers.renderImage(productData.secondary_image, 500);
    if (imageFromProductData.sizes === '' || imageFromProductData.srcset === '') {
      imageFromProductData.src = window.fallback_logo_url;
    }
    
    const secondaryImage = document.createElement('img');
    secondaryImage.className = 'product-card__image-secondary';
    secondaryImage.src = imageFromProductData.src;
    secondaryImage.srcset = imageFromProductData.srcset;
    secondaryImage.sizes = imageFromProductData.sizes;
    secondaryImage.alt = `${productCard.querySelector('.product-card__title')?.textContent || 'Product'} - ${window.algoliaLocales?.product?.worn_view}`;
    secondaryImage.loading = 'lazy';
    
    imageContainer.appendChild(secondaryImage);

    // if the second picture SRC does not contain "LB1" or "LS1", disable the worn image toggle
    const secondaryImageSrc = productData.secondary_image.product_image;
    if (!secondaryImageSrc?.includes('LB1') && !secondaryImageSrc?.includes('LS1') && !secondaryImageSrc?.includes('LB2') && !secondaryImageSrc?.includes('LS2')  ) {
    // if (!secondaryImageSrc.includes('LB1') && !secondaryImageSrc.includes('LS1')  ) {
      imageContainer.classList.add('product-card__images-container--no-toggle');
    }
  },

  handleVariantData(cardElement, productData) {

    // console.log('🔍 Handling variant data for', cardElement);
    // console.log('🔍 Handling variant data', productData);

    if (!cardElement || !productData) return;

    const addToCartText = window.algoliaLocales?.product?.add_to_cart;
    const selectSizeText = window.algoliaLocales?.product?.select_size;

    // --- RESET STATE ---
    const elementsToReset = cardElement.querySelectorAll(' .product-card__text-badges, .product-card__out-of-stock, .product-card__sizes-desktop, .product-card__sizes--mobile');
    elementsToReset.forEach(el => {
      el.classList.remove('algolia-visible');
      el.classList.add('algolia-hidden');
    });

    const variantData = productData.variants;
    if (!variantData) return;

    // Determine if tooltips should be shown
    const pricesVary = cardElement.dataset.pricesVary === 'true';
    const showTooltips = !this.TOOLTIP_CONFIG.show_on_price_variation_only || pricesVary;
    let currency = cardElement.dataset.currency || '';
    const isCurrencyDifferent = currency.toLowerCase() !== Shopify.currency.active.toLowerCase();
    // console.log("🚀 ~ isCurrencyDifferent:", isCurrencyDifferent)

    const priceElement = cardElement.querySelector('.product-card__price-current');
    const comparePriceElement = cardElement.querySelector('.product-card__price-original');
    
    // Get both available and all variants for different displays
    const allVariants = variantData; // Show all variants in mobile
    if (allVariants.length === 0) return;

    if (isCurrencyDifferent) {
      currency = Shopify.currency.active;

      // Update main product prices from variant data when currencies differ
      if (priceElement || comparePriceElement) {
        let lowestPrice = null;
        let lowestComparePrice = null;

        // Find the lowest price and compare price from available variants
        allVariants.forEach(variant => {
          const price = variant.price ? parseFloat(variant.price) / 100 : null;
          const comparePrice = variant.compare_at_price ? parseFloat(variant.compare_at_price) / 100 : null;

          if (price !== null) {
            if (lowestPrice === null || price < lowestPrice) {
              lowestPrice = price;
            }
          }

          if (comparePrice !== null) {
            if (lowestComparePrice === null || comparePrice < lowestComparePrice) {
              lowestComparePrice = comparePrice;
            }
          }
        });

        // Update price element
        if (priceElement && lowestPrice !== null) {
          priceElement.textContent = this.formatPriceWithCurrency(lowestPrice, currency);
        }

        // Update compare price element
        if (comparePriceElement) {
          if (lowestComparePrice !== null && lowestComparePrice > lowestPrice) {
            comparePriceElement.textContent = this.formatPriceWithCurrency(lowestComparePrice, currency);
            comparePriceElement.style.display = 'inline';
          } else {
            comparePriceElement.style.display = 'none';
          }
        }
      }
    }
    
    const atcContainer = cardElement.querySelector('.product-card__atc-container');
    const atcButton = cardElement.querySelector('.product-card__atc-button');
    const variantsDesktopContainer = cardElement.querySelector('.product-card__sizes-desktop');
    
    // --- SINGLE VARIANT LOGIC ---
    // For single variants, we still want to show the variant selector
    // and pre-select the variant, so we don't return early anymore

    // --- MULTI-VARIANT LOGIC ---
    const variantsDesktopList = cardElement.querySelector('.product-card__sizes-list');

    if (variantsDesktopList) variantsDesktopList.innerHTML = '';

    let hasAvailableVariants = false;

    // ========================================
    // Reset button states to initial state
    // Critical for browser back button (bfcache)
    // ========================================
    const klaviyoContainer = cardElement.querySelector('.klaviyo-product-container');
    
    // Start with clean state
    if (klaviyoContainer) {
      klaviyoContainer.classList.add('hidden'); // Initially hidden
    }
    if (atcButton) {
      // Remove all state classes to ensure clean slate
      atcButton.classList.remove('hidden');
      atcButton.classList.remove('product-card__atc-button--disabled');
      
      // Set initial state
      atcButton.disabled = true;
      atcButton.textContent = window.algoliaLocales?.product?.select_size || 'Select size';
      delete atcButton.dataset.variantId;
      delete atcButton.dataset.variantAvailable;
    }

    // Get mobile container (the flex wrapper that contains label and sizes)
    const mobileContainer = cardElement.querySelector('.product-card__sizes--mobile');
    
    // Clear any existing mobile size elements (keep only the label)
    if (mobileContainer) {
      const label = mobileContainer.querySelector('.product-card__sizes-label');
      mobileContainer.innerHTML = '';
      if (label) mobileContainer.appendChild(label);
    }

    // Process all variants for both desktop and mobile
    allVariants.forEach(variant => {
      const isAvailable = variant.available === 'true' || variant.available === true;
      if (isAvailable) hasAvailableVariants = true;

      // Desktop variant button (ALL variants - available and unavailable)
      const variantButton = document.createElement('button');
      variantButton.type = 'button';
      variantButton.className = `product-card__size-button ${isAvailable ? '' : 'product-card__size-button--unavailable'}`;
      variantButton.textContent = variant.title;
      variantButton.dataset.variantId = variant.id;
      variantButton.dataset.available = isAvailable.toString();
      // Keep disabled for truly unavailable variants, but allow selection of OOS variants
      if(!isAvailable) variantButton.disabled = false;

      if (showTooltips && variant.price) {
        // const formattedPrice = this.formatPriceWithCurrency(variant.price / 100, currency);
        const formattedPrice = this.formatPriceWithCurrency(variant.price / 100, currency);
        variantButton.classList.add('has-price-tooltip');
        variantButton.dataset.tooltipPrice = formattedPrice;
        variantButton.dataset.tooltipComparePrice = variant.compare_at_price ? this.formatPriceWithCurrency(variant.compare_at_price / 100, currency) : '';
      }

      if (variantsDesktopList) variantsDesktopList.appendChild(variantButton);

      // Mobile variant element (all variants with different styling)
      // Append directly to mobile container at same level as label
      if (mobileContainer) {
        const variantElement = document.createElement('span');
        variantElement.className = `product-card__size-mobile ${isAvailable ? 'product-card__size-mobile--available' : 'product-card__size-mobile--unavailable'}`;
        variantElement.textContent = variant.title;
        
        // Add link only for available variants
        if (isAvailable) {
          const variantLink = document.createElement('a');
          variantLink.href = `${window.Shopify.routes.root}products/${cardElement.dataset.handle}?variant=${variant.id}`;
          variantLink.className = 'product-card__size-mobile-link';
          variantLink.appendChild(variantElement);
          mobileContainer.appendChild(variantLink);
        } else {
          mobileContainer.appendChild(variantElement);
        }
      }
    });

    const variantsDesktop = cardElement.querySelector('.product-card__sizes-desktop');
    if (variantsDesktop) variantsDesktop.classList.add('algolia-visible');

    const sizesMobile = cardElement.querySelector('.product-card__sizes--mobile');
    if (sizesMobile && allVariants.length > 0) {
      sizesMobile.classList.add('algolia-visible');
    }

    const outOfStockOverlay = cardElement.querySelector('.product-card__out-of-stock');

    // Show badges if there are any available variants
    if (hasAvailableVariants) {
      // const iconBadges = cardElement.querySelector('.product-card__icon-badges');
      const textBadges = cardElement.querySelector('.product-card__text-badges');
      // if(iconBadges) iconBadges.classList.add('algolia-visible');
      if(textBadges) textBadges.classList.add('algolia-visible');
    } else if (allVariants.length > 0) {
      // Show OOS overlay if all variants are unavailable
      if(outOfStockOverlay) {
        const hasComingSoonTag = cardElement.dataset.tags && cardElement.dataset.tags.includes('coming_soon');
        const oosText = hasComingSoonTag
          ? window.algoliaLocales?.product?.coming_soon
          : window.algoliaLocales?.product?.out_of_stock;
        outOfStockOverlay.textContent = oosText;
        outOfStockOverlay.classList.add('algolia-visible');
        const lhgBadges = cardElement.querySelector('.lhg-app-badge-anchor');
        if(lhgBadges) lhgBadges.classList.add('algolia-hidden');
      }
    }

    // Handle initial Klaviyo button state on page load
    // Exception: On product template, Klaviyo only works for main product, not for cards
    const isProductTemplate = document.body.classList.contains('template-product');
    
    if (!isProductTemplate) {
      // Not product template - use Klaviyo logic
      if (!hasAvailableVariants) {
        // All variants unavailable - show Klaviyo, hide default ATC
        window.klaviyoProductHelpers.showKlaviyoHideDefault(cardElement);
      } else {
        // At least one variant available - show default "Choose variant", hide Klaviyo
        window.klaviyoProductHelpers.showDefaultHideKlaviyo(cardElement);
      }
    } else {
      // Product template - just show Out of Stock state, no Klaviyo
      if (!hasAvailableVariants && atcButton) {
        const hasComingSoonTag = cardElement.dataset.tags && cardElement.dataset.tags.includes('coming_soon');
        const oosText = hasComingSoonTag
          ? window.algoliaLocales?.product?.coming_soon
          : window.algoliaLocales?.product?.out_of_stock;
        atcButton.textContent = oosText;
        atcButton.disabled = true;
      }
    }

    // --- AUTO-SELECT SINGLE VARIANT ---
    // For products with only 1 variant, auto-select it to show the correct button state immediately
    if (allVariants.length === 1) {
      const singleVariantButton = variantsDesktopList?.querySelector('.product-card__size-button');
      if (singleVariantButton && atcButton) {
        // Directly select the single variant (same logic as click handler)
        const allVariantButtons = cardElement.querySelectorAll('.product-card__size-button');
        allVariantButtons.forEach(btn => btn.classList.remove('active'));

        singleVariantButton.classList.add('active');
        const isVariantAvailable = singleVariantButton.dataset.available === 'true';

        atcButton.dataset.variantId = singleVariantButton.dataset.variantId;
        atcButton.dataset.variantAvailable = isVariantAvailable.toString();

        if (isVariantAvailable) {
          atcButton.textContent = addToCartText;
          atcButton.disabled = false;
        } else {
          // OOS variant selected - show appropriate OOS text
          const hasComingSoonTag = cardElement.dataset.tags && cardElement.dataset.tags.includes('coming_soon');
          const oosText = hasComingSoonTag
            ? window.algoliaLocales?.product?.coming_soon
            : window.algoliaLocales?.product?.out_of_stock;

          atcButton.textContent = oosText;
          atcButton.disabled = true;
        }
      }
    }

    // --- ATC Button Logic ---
    // Show ATC container for products with any variants (available or OOS)
    if (variantsDesktopContainer && allVariants.length > 0) {
      variantsDesktopContainer.classList.add('algolia-visible');
    }

    if (atcContainer && atcButton && allVariants.length > 0) {
      atcContainer.style.display = 'block';

      // For single variants, the button state is already set by auto-selection logic above
      // For multi-variants, set the initial state
      if (allVariants.length > 1) {
        // If all variants are unavailable, show OOS text immediately
        if (!hasAvailableVariants) {
          const hasComingSoonTag = cardElement.dataset.tags && cardElement.dataset.tags.includes('coming_soon');
          const oosText = hasComingSoonTag
            ? window.algoliaLocales?.product?.coming_soon
            : window.algoliaLocales?.product?.out_of_stock;
          atcButton.textContent = oosText;
          atcButton.disabled = true;
        } else {
          atcButton.textContent = selectSizeText;
          // Button remains disabled until a variant is selected via event listener
        }
      }
    }
  },

  setupImageToggle() {
    const toggleSwitch = document.getElementById('product-worn-toggle');
    if (!toggleSwitch || toggleSwitch.dataset.initialized === 'true') return;
  
    
    toggleSwitch.addEventListener('change', function() {
      document.body.classList.toggle('worn-view-active', this.checked);

      document.querySelectorAll('.ais-hit-custom.ais-product .product-card__images-container:not(.product-card__images-container--no-toggle)').forEach(imageScrollContainer => {
        if (imageScrollContainer.scrollWidth > imageScrollContainer.clientWidth) {
          if (this.checked) {
            imageScrollContainer.scrollLeft = imageScrollContainer.clientWidth;
          } else {
            imageScrollContainer.scrollLeft = 0;
          }
        }
      });
    });

    this.setupQuickAddListeners();
    
    toggleSwitch.dataset.initialized = 'true';
  },

  setupQuickAddListeners() {
    if (this._quickAddInitialized) return;
    this._quickAddInitialized = true;

    const hitsContainer = document.querySelector('.ais-hits-container');
    if (!hitsContainer) return;
    
    const errorText = window.algoliaLocales?.product?.cart_error;
    const addToCartText = window.algoliaLocales?.product?.add_to_cart;
    const selectSizeText = window.algoliaLocales?.product?.select_size;

    // Delegated listener for variant selection
    hitsContainer.addEventListener('click', (event) => {
      const variantButton = event.target.closest('.product-card__size-button');
      if (!variantButton || variantButton.disabled) return;

      event.preventDefault();

      const productCard = variantButton.closest('.ais-product');
      const atcButton = productCard.querySelector('.product-card__atc-button');
      const outOfStockOverlay = productCard.querySelector('.product-card__out-of-stock');
      const wasActive = variantButton.classList.contains('active');
      const productPriceContainer = productCard.querySelector('.product-card__price');
      // console.log("🚀 ~ productPriceContainer:", productPriceContainer)

      // Update active state
      const allVariantButtons = productCard.querySelectorAll('.product-card__size-button');
      allVariantButtons.forEach(btn => btn.classList.remove('active'));

      if (wasActive) {
        // Toggle off - back to "Choose variant" state
        atcButton.textContent = selectSizeText;
        atcButton.disabled = true;
        delete atcButton.dataset.variantId;
        delete atcButton.dataset.variantAvailable;
        
        // Restore original price container
        this.updatePriceContainer(productPriceContainer, null, true);

        // Show default button, hide Klaviyo (back to initial state for products with available variants)
        // Exception: On product template, don't use Klaviyo for product cards
        const isProductTemplate = document.body.classList.contains('template-product');
        if (!isProductTemplate) {
          window.klaviyoProductHelpers.showDefaultHideKlaviyo(productCard);
        }
      } else {
        // Toggle on
        variantButton.classList.add('active');
        const isVariantAvailable = variantButton.dataset.available === 'true';

        atcButton.dataset.variantId = variantButton.dataset.variantId;
        atcButton.dataset.variantAvailable = isVariantAvailable.toString();
        
        // Update price container
        if (variantButton.dataset.tooltipPrice) {
          this.updatePriceContainer(productPriceContainer, variantButton, false);
        }


        if (isVariantAvailable) {
          atcButton.textContent = addToCartText;
          atcButton.disabled = false;
        } else {
          // OOS variant selected - show appropriate OOS text
          const hasComingSoonTag = productCard.dataset.tags && productCard.dataset.tags.includes('coming_soon');
          const oosText = hasComingSoonTag
            ? window.algoliaLocales?.product?.coming_soon
            : window.algoliaLocales?.product?.out_of_stock;

          atcButton.textContent = oosText;
          atcButton.disabled = true;

          // Don't show OOS overlay for individual OOS variant selection
        }

        // Update Klaviyo button visibility based on variant availability
        // Exception: On product template, don't use Klaviyo for product cards
        const isProductTemplate = document.body.classList.contains('template-product');
        if (!isProductTemplate) {
          window.klaviyoProductHelpers.updateKlaviyoVisibility(productCard, isVariantAvailable);
        }
      }

      // Clear any previous errors
      const errorContainer = productCard.querySelector('.product-card__atc-error');
      if(errorContainer) errorContainer.textContent = '';
    });

    // Delegated listener for Add to Cart button
    hitsContainer.addEventListener('click', async (event) => {
      const atcButton = event.target.closest('.product-card__atc-button');
      if (!atcButton || atcButton.disabled) return;

      // Prevent adding OOS variants to cart
      const isVariantAvailable = atcButton.dataset.variantAvailable === 'true';
      if (!isVariantAvailable) return;

      event.preventDefault();
      
      const variantId = atcButton.dataset.variantId;
      if (!variantId) return;

      const productCard = atcButton.closest('.ais-product');
      const errorContainer = productCard.querySelector('.product-card__atc-error');
      if(errorContainer) errorContainer.textContent = '';
      atcButton.classList.add('loading');
      atcButton.disabled = true;

      try {
        const itemData = {
          id: variantId,
          quantity: 1
        };

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [itemData],
            sections: ['cart-drawer', 'cart-icon-bubble']
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Request failed');
        }

        const data = await response.json();
        const gwpResult = await window.processGWP(data, 'cart-add');
        console.log("🚀 ~ gwpResult:", gwpResult)

        // New cart: re-render via the cart-drawer component, then open it.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:open'));

      } catch (error) {
        console.error('Error adding to cart:', error);
        if(errorContainer) errorContainer.textContent = errorText;
      } finally {
        atcButton.classList.remove('loading');
        // Re-enable based on selected variant, or keep disabled if error
        const selectedVariant = productCard.querySelector('.product-card__size-button.active');
        if (selectedVariant) {
          atcButton.disabled = false;
        }
      }
    });
  },

  /**
   * Updates the price container when a variant is selected or deselected
   * @param {HTMLElement} priceContainer - The product card price container
   * @param {HTMLElement|null} variantButton - The variant button that was clicked (null when restoring)
   * @param {boolean} restore - Whether to restore the original prices
   */
  updatePriceContainer(priceContainer, variantButton, restore = false) {
    if (!priceContainer) return;

    // Store original prices on first interaction
    if (!priceContainer.dataset.originalPrice) {
      const currentPriceEl = priceContainer.querySelector('.product-card__price-current');
      const originalPriceEl = priceContainer.querySelector('.product-card__price-original');
      const discountEl = priceContainer.querySelector('.product-card__price-discount:not([class*="badge"])');
      const fromEl = priceContainer.querySelector('.product-card__price-from');
      
      
      if (currentPriceEl) {
        priceContainer.dataset.originalPrice = currentPriceEl.textContent;
      }
      if (originalPriceEl) {
        priceContainer.dataset.originalComparePrice = originalPriceEl.textContent;
      }
      if (discountEl) {
        priceContainer.dataset.originalDiscount = discountEl.textContent;
      }
      priceContainer.dataset.hadFrom = fromEl ? 'true' : 'false';
      priceContainer.dataset.hadComparePrice = originalPriceEl ? 'true' : 'false';
    }

    const currentPriceEl = priceContainer.querySelector('.product-card__price-current');
    const originalPriceEl = priceContainer.querySelector('.product-card__price-original');
    const discountEl = priceContainer.querySelector('.product-card__price-discount:not([class*="badge"])');
    const fromEl = priceContainer.querySelector('.product-card__price-from');
    const saleBadgeEl = priceContainer.querySelector('.product-card__price-badge--sale');
    // console.log("🚀 ~ saleBadgeEl:", saleBadgeEl)

    if (restore) {
      // Restore original prices
      if (currentPriceEl && priceContainer.dataset.originalPrice) {
        currentPriceEl.textContent = priceContainer.dataset.originalPrice;
      }

      // Restore or remove "From" label
      if (priceContainer.dataset.hadFrom === 'true') {
        if (!fromEl) {
          const fromLabel = document.createElement('span');
          fromLabel.className = 'product-card__price-from';
          fromLabel.textContent = window.algoliaLocales?.product?.from_label || 'From';
          priceContainer.insertBefore(fromLabel, currentPriceEl);
        } else {
          fromEl.style.display = '';
        }
      }

      // Restore original compare price and discount
      if (priceContainer.dataset.hadComparePrice === 'true') {
        if (originalPriceEl) {
          originalPriceEl.textContent = priceContainer.dataset.originalComparePrice || '';
          originalPriceEl.style.display = '';
        }
        if (discountEl) {
          discountEl.textContent = priceContainer.dataset.originalDiscount || '';
          discountEl.style.display = '';
        }
      } else {
        // Hide compare price and discount if they weren't there originally
        if (originalPriceEl) {
          originalPriceEl.style.display = 'none';
        }
        if (discountEl) {
          discountEl.style.display = 'none';
        }
      }

      return;
    }

    // Update prices based on selected variant
    if (!variantButton) return;

    const variantPrice = variantButton.dataset.tooltipPrice;
    const variantComparePrice = variantButton.dataset.tooltipComparePrice;

    // Update current price
    if (currentPriceEl && variantPrice) {
      currentPriceEl.textContent = variantPrice;
    }

    // Hide "From" label when specific variant is selected
    if (fromEl) {
      fromEl.style.display = 'none';
    }

    // Update compare price and discount
    if (variantComparePrice && variantComparePrice.trim() !== '') {
      // Parse prices to calculate discount
      const parsePrice = (priceStr) => {
        const match = priceStr.match(/[\d,]+\.?\d*/);
        return match ? parseFloat(match[0].replace(',', '')) : 0;
      };

      const currentPrice = parsePrice(variantPrice);
      const comparePrice = parsePrice(variantComparePrice);

      if (comparePrice > currentPrice) {
        // Show compare price
        if (originalPriceEl) {
          originalPriceEl.textContent = variantComparePrice;
          originalPriceEl.style.display = '';
        } else {
          // Create compare price element if it doesn't exist
          const newOriginalPriceEl = document.createElement('span');
          newOriginalPriceEl.className = 'product-card__price-original';
          newOriginalPriceEl.textContent = variantComparePrice;
          currentPriceEl.after(newOriginalPriceEl);
        }

        // Calculate and show discount percentage
        const discountPercent = Math.round(((comparePrice - currentPrice) / comparePrice) * 100);
        if (discountEl) {
          discountEl.textContent = `-${discountPercent}%`;
          discountEl.style.display = '';
        } else {
          // Create discount element if it doesn't exist
          const newDiscountEl = document.createElement('span');
          newDiscountEl.className = 'product-card__price-discount';
          newDiscountEl.textContent = `-${discountPercent}%`;
          const insertAfter = priceContainer.querySelector('.product-card__price-original');
          if (insertAfter) {
            insertAfter.after(newDiscountEl);
          } else {
            currentPriceEl.after(newDiscountEl);
          }
        }
        if (saleBadgeEl) {
          saleBadgeEl.style.display = '';
        }
      } else {
        // Hide compare price and discount if compare price is not greater
        if (originalPriceEl) {
          originalPriceEl.style.display = 'none';
        }
        if (discountEl) {
          discountEl.style.display = 'none';
        }
      }
    } else {
      // No compare price for this variant - hide compare price elements
      if (originalPriceEl) {
        originalPriceEl.style.display = 'none';
      }
      if (discountEl) {
        discountEl.style.display = 'none';
      }
      if (saleBadgeEl) {
        saleBadgeEl.style.display = 'none';
      }
    }
  }
}; 